import { Stack, Typography, Box, CircularProgress } from "@mui/material";
import { FC, useCallback, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/elements/Button";
import { IconArrowBack } from "@/assets/icons";
import { text, background, divider, primary, icon } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { spacing, rounded } from "@/theme/variables";
import { TStudySubject, TLevelDiagnosis, useDiagnosisQuizMetadata } from "@/infrastructure/adapters/study";
import { isApiError, showError } from "@/utils/error";
import { ERROR_STATUS_CODES } from "@/constants/error";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useQueryClient } from "@tanstack/react-query";
import { useStudyAccessGuard } from "@/providers/StudyAccessGuardProvider";
import { AppRoutes } from "@/routes/config";
import { LoadingArea } from "@/components/elements/Loading/Loading";

export const StudySubjectPage: FC = () => {
  const { subject } = useStudyAccessGuard();
  const {
    isLoading: isLoadingDiagnosisQuizMetadata,
    levelDiagnosis,
    skipLevelAssessment,
  } = useDiagnosisQuizMetadata(subject.id, !subject.hasLevelAssessment);

  if (isLoadingDiagnosisQuizMetadata) {
    return <LoadingArea />;
  }

  if (levelDiagnosis) {
    return <LevelDiagnosisPage subject={subject} diagnosis={levelDiagnosis} onSkipLevelAssessment={skipLevelAssessment} />;
  }

  // No diagnosis metadata available — fallback to study list
  return <Navigate to="/study" replace />;
};

const bulletStyle = {
  ...typography.body14Semi,
  color: text.secondary,
} as const;

/** STUDY_L_Learn-Lv1-1: Level Diagnosis Test page */
type TLevelDiagnosisPageProps = {
  subject: TStudySubject;
  diagnosis: TLevelDiagnosis;
  onSkipLevelAssessment: () => Promise<{ subjectId: number; level: string }>;
};
const LevelDiagnosisPage: FC<TLevelDiagnosisPageProps> = ({ subject, diagnosis, onSkipLevelAssessment }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleBack = useCallback(() => {
    navigate("/study");
  }, [navigate]);

  const handleStartDiagnosis = useCallback(() => {
    navigate(`/study/${subject.id}/diagnosis`);
  }, [navigate, subject.id]);

  const [isSkipping, setIsSkipping] = useState(false);

  const handleSkip = useCallback(async () => {
    const invalidateAndNavigate = (to: string = `/study/${subject.id}/contents`, navigateOptions: { replace?: boolean } = {}) => {
      queryClient.removeQueries({ queryKey: [QUERY_KEYS.STUDY.PAGE_DATA, language] });
      navigate(to, navigateOptions);
    };

    setIsSkipping(true);
    try {
      await onSkipLevelAssessment();
      invalidateAndNavigate();
    } catch (error) {
      if (isApiError(error)) {
        const code = error.code;
        if (code === ERROR_STATUS_CODES.LEARNER_SUBJECT_LEVEL_ALREADY_EXISTS) {
          invalidateAndNavigate();
          return;
        }
        if (code === ERROR_STATUS_CODES.INDUSTRY_SUBJECT_NOT_EXISTS) {
          invalidateAndNavigate(AppRoutes.study, { replace: true });
          showError(error);
          return;
        }
      }
      showError(error);
    } finally {
      setIsSkipping(false);
    }
  }, [navigate, subject.id, queryClient, language]);

  return (
    <Stack sx={{ flex: 1 }}>
      {/* Back button row */}
      <Box
        onClick={handleBack}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          py: 1,
          px: 2,
          cursor: "pointer",
        }}
      >
        <IconArrowBack style={{ fontSize: 16, color: icon.default }} />
        <Typography sx={{ ...typography.cap12Bold, color: text.secondary }}>{t("common.back")}</Typography>
      </Box>

      {/* Content container */}
      <Box
        sx={{
          py: spacing.sm,
          px: 2,
          bgcolor: background.white,
          borderBottom: `1px solid ${divider.high}`,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        {/* Title + description block */}
        <Stack sx={{ gap: 1.5 }}>
          <Typography sx={{ ...typography.body14Bold, color: text.primary }}>{t("study.levelDiagnosis.title")}</Typography>

          <Typography sx={{ ...typography.body14, color: text.primary, whiteSpace: "pre-line" }}>{t("study.levelDiagnosis.description")}</Typography>

          {/* Bullet items — data from mock */}
          <Stack component="ul" sx={{ m: 0, pl: 2, listStyleType: '"・"' }}>
            <Typography component="li" sx={bulletStyle}>
              {t("study.levelDiagnosis.totalQuestions", { count: diagnosis.totalQuestionCount })}
            </Typography>
            <Typography component="li" sx={bulletStyle}>
              {t("study.levelDiagnosis.estimatedTime", { count: diagnosis.estimatedMinutes })}
            </Typography>
          </Stack>
        </Stack>

        {/* Primary button: Start diagnosis */}
        <Button variant="primary" fullWidth onClick={handleStartDiagnosis}>
          <Typography component="span" variant="body14Semi">
            {t("study.levelDiagnosis.startDiagnosis")}
          </Typography>
        </Button>

        {/* Skip button */}
        <Box
          onClick={!isSkipping ? handleSkip : undefined}
          sx={{
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            border: `2px solid ${primary[500]}`,
            borderRadius: rounded.sm,
            bgcolor: background.white,
            cursor: isSkipping ? "default" : "pointer",
            opacity: isSkipping ? 0.6 : 1,
          }}
        >
          {isSkipping && <CircularProgress size={16} sx={{ color: primary[500] }} />}
          <Typography sx={{ ...typography.body14Semi, color: primary[500] }}>{t("study.levelDiagnosis.skipAndStart")}</Typography>
        </Box>
      </Box>
    </Stack>
  );
};

export default StudySubjectPage;
